import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
} from '@fluentui/react-components';
import { Settings20Regular } from '@fluentui/react-icons';
import { useAppStore } from '@/stores/chatbotStore';
import { apiClient } from '@/services/api';
import ChatInterface from '@/components/ChatInterface';
import ChatbotManager from '@/components/ChatbotManager';
import ChatbotSettingsDialog from '@/components/ChatbotSettingsDialog';
import { Conversation } from '@/types/api';
import logoAlpilink from '@/assets/alpilink-groupe-bleu-vf-1024x437.png';

/* ------------------------------------------------------------------ */
/*  Date-grouping helper                                               */
/* ------------------------------------------------------------------ */
function groupConversationsByDate(conversations: Conversation[]) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 7 * 86400000);

  const groups: { label: string; items: Conversation[] }[] = [
    { label: "Aujourd'hui", items: [] },
    { label: 'Hier', items: [] },
    { label: 'Cette semaine', items: [] },
    { label: 'Plus ancien', items: [] },
  ];

  for (const conv of conversations) {
    const date = new Date(conv.updated_at || conv.created_at);
    if (date >= today) groups[0].items.push(conv);
    else if (date >= yesterday) groups[1].items.push(conv);
    else if (date >= weekAgo) groups[2].items.push(conv);
    else groups[3].items.push(conv);
  }

  return groups.filter((g) => g.items.length > 0);
}

/* ------------------------------------------------------------------ */
/*  Dashboard                                                          */
/* ------------------------------------------------------------------ */
const Dashboard: React.FC = () => {
  const {
    chatbots,
    currentChatbot,
    conversations,
    currentConversation,
    isStreaming,
    sidebarOpen,
    toggleSidebar,
    loadChatbots,
    loadConversations,
    loadDocuments,
    createConversation,
    selectConversation,
    loadConversationMessages,
    deleteConversation,
    loadAppInfo,
    appInfo,
  } = useAppStore();

  const [chatbotSelectorOpen, setChatbotSelectorOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hoveredConvId, setHoveredConvId] = useState<string | null>(null);
  const [deleteConvId, setDeleteConvId] = useState<string | null>(null);

  /* ---------- Bootstrap ---------- */
  useEffect(() => {
    loadAppInfo();
    loadChatbots();
  }, [loadAppInfo, loadChatbots]);

  // Auto-select the default chatbot (mono-chatbot mode): land directly in the chat
  useEffect(() => {
    if (chatbots.length >= 1 && !currentChatbot) {
      useAppStore.getState().selectChatbot(chatbots[0]);
    }
  }, [chatbots, currentChatbot]);

  // When a chatbot is selected, fetch its conversations
  useEffect(() => {
    if (currentChatbot) {
      loadConversations(currentChatbot.id);
    }
  }, [currentChatbot, loadConversations]);

  // Documents are scoped per conversation: (re)load them when the conversation changes
  useEffect(() => {
    if (currentConversation) {
      loadDocuments(currentConversation.id);
    }
  }, [currentConversation, loadDocuments]);

  /* ---------- Handlers ---------- */
  const handleSignOut = async () => {
    try {
      await apiClient.logout();
    } finally {
      window.location.reload();
    }
  };

  const handleNewChat = async () => {
    if (!currentChatbot) return;
    try {
      await createConversation(currentChatbot.id, 'Nouvelle conversation');
    } catch (err) {
      console.error('Failed to create conversation:', err);
    }
  };

  const handleSelectConversation = async (conv: Conversation) => {
    selectConversation(conv);
    await loadConversationMessages(conv.id);
    // Close sidebar on mobile after selecting
    if (window.innerWidth <= 768) {
      toggleSidebar();
    }
  };

  const handleDeleteConversation = (
    e: React.MouseEvent,
    convId: string,
  ) => {
    e.stopPropagation();
    if (isStreaming) return; // streaming guard
    setDeleteConvId(convId);
  };

  const confirmDeleteConversation = async () => {
    if (!deleteConvId) return;
    try {
      await deleteConversation(deleteConvId);
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
    setDeleteConvId(null);
  };

  /* ---------- Derived data ---------- */
  const sortedConversations = [...conversations].sort(
    (a, b) =>
      new Date(b.updated_at || b.created_at).getTime() -
      new Date(a.updated_at || a.created_at).getTime(),
  );
  const groups = groupConversationsByDate(sortedConversations);

  /* ---------- Render ---------- */
  return (
    <div className="app-layout">
      {/* Sidebar toggle button */}
      <button
        className={`sidebar-toggle ${sidebarOpen ? 'sidebar-open' : ''}`}
        onClick={toggleSidebar}
        aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
      >
        {sidebarOpen ? '\u2715' : '\u2630'}
      </button>

      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
        onClick={toggleSidebar}
      />

      {/* ==================== Sidebar ==================== */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${!sidebarOpen ? 'collapsed' : ''}`}>
        {/* --- Header --- */}
        <div className="sidebar-header">
          <span className="sidebar-logo-wrap">
            <img src={logoAlpilink} alt="Alpilink" className="sidebar-logo" />
          </span>
          <div className="sidebar-header-row">
            {currentChatbot && (
              <button className="sidebar-new-chat" onClick={handleNewChat}>
                + Nouveau chat
              </button>
            )}
          </div>
        </div>

        {/* --- Conversation list --- */}
        <div className="sidebar-conversations">
          {!currentChatbot ? (
            <p
              style={{
                padding: '16px 8px',
                fontSize: '13px',
                color: 'var(--color-sidebar-text-muted)',
              }}
            >
              Sélectionnez un chatbot pour voir vos conversations.
            </p>
          ) : groups.length === 0 ? (
            <p
              style={{
                padding: '16px 8px',
                fontSize: '13px',
                color: 'var(--color-sidebar-text-muted)',
              }}
            >
              Aucune conversation. Cliquez sur &laquo;&nbsp;+ Nouveau
              chat&nbsp;&raquo; pour commencer.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.label} style={{ marginBottom: '8px' }}>
                <div
                  style={{
                    padding: '6px 12px',
                    fontSize: '11px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--color-sidebar-text-muted)',
                  }}
                >
                  {group.label}
                </div>

                {group.items.map((conv) => (
                  <div
                    key={conv.id}
                    role="button"
                    tabIndex={0}
                    className={`conversation-item ${
                      currentConversation?.id === conv.id ? 'active' : ''
                    }`}
                    onClick={() => handleSelectConversation(conv)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectConversation(conv); }}
                    onMouseEnter={() => setHoveredConvId(conv.id)}
                    onMouseLeave={() => setHoveredConvId(null)}
                    title={conv.title}
                  >
                    <span className="conversation-title">{conv.title}</span>

                    {hoveredConvId === conv.id && (
                      <button
                        className="conversation-delete"
                        disabled={isStreaming}
                        onClick={(e) => handleDeleteConversation(e, conv.id)}
                        title="Supprimer la conversation"
                        style={{
                          opacity: 1,
                          cursor: isStreaming ? 'not-allowed' : 'pointer',
                        }}
                      >
                        &#x2715;
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* --- Chatbot selector (visible only if more than 1 chatbot) --- */}
        {chatbots.length > 1 && (
        <div className="chatbot-selector">
          <button
            onClick={() => setChatbotSelectorOpen(!chatbotSelectorOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              background: 'transparent',
              border: 'none',
              color: 'var(--color-sidebar-text)',
              padding: '8px 0',
              fontSize: '13px',
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--color-sidebar-text-muted)',
              }}
            >
              Chatbots
              {currentChatbot ? ` — ${currentChatbot.name}` : ''}
            </span>
            <span
              style={{
                transition: 'transform 0.2s',
                transform: chatbotSelectorOpen
                  ? 'rotate(180deg)'
                  : 'rotate(0deg)',
                fontSize: '12px',
                color: 'var(--color-sidebar-text-muted)',
              }}
            >
              &#x25B2;
            </span>
          </button>

          {chatbotSelectorOpen && (
            <div
              style={{
                maxHeight: '300px',
                overflowY: 'auto',
                marginTop: '4px',
              }}
            >
              <ChatbotManager />
            </div>
          )}
        </div>
        )}

        {/* --- Alpilink footer --- */}
        <div className="sidebar-footer">
          {currentChatbot && (
            <button className="sidebar-settings" onClick={() => setSettingsOpen(true)}>
              <Settings20Regular />
              <span>Réglages du chatbot</span>
            </button>
          )}
          <button className="sidebar-signout" onClick={handleSignOut}>
            Se déconnecter
          </button>
          <span>Propulsé par l'équipe IA d'Alpilink</span>
        </div>
      </aside>

      <ChatbotSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      {/* ==================== Main content ==================== */}
      <main className="main-content">
        {!currentChatbot ? (
          /* --- Welcome: no chatbot selected --- */
          <div className="welcome-screen">
            <h1 className="welcome-title">{appInfo?.name ?? 'Chatbot Alpilink'}</h1>
            <p className="welcome-subtitle">
              Démarrage de l'assistant…
            </p>
          </div>
        ) : (
          /* --- Chatbot selected: show ChatInterface --- */
          <ChatInterface />
        )}
      </main>

      {/* Delete conversation confirmation dialog */}
      <Dialog open={!!deleteConvId} onOpenChange={(_, data) => { if (!data.open) setDeleteConvId(null); }}>
        <DialogSurface>
          <DialogTitle>Supprimer la conversation</DialogTitle>
          <DialogContent>
            <DialogBody>
              Supprimer cette conversation ? Cette action est irréversible.
            </DialogBody>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setDeleteConvId(null)}>Annuler</Button>
            <Button appearance="primary" style={{ background: '#d13438' }} onClick={confirmDeleteConversation}>Supprimer</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default Dashboard;
